import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import addMonths from 'date-fns/addMonths';
import endOfMonth from 'date-fns/endOfMonth';
import formatISO from 'date-fns/formatISO';
import startOfMonth from 'date-fns/startOfMonth';
import subMonths from 'date-fns/subMonths';
import { Observable } from 'rxjs';
import {
  AutoResolveChange,
  B2BComment,
  CartSimulation,
  OpeningSchedule,
  Order,
  OrderEntry,
  OrderMergeProposal,
  Reinvoicing,
  ResolveOptionActionType,
} from '../../../../core/model';
import { Occ } from '../../../../core/occ';
import { ConverterService } from '../../../../core/util';
import { CART_SIMULATION_NORMALIZER, ORDER_NORMALIZER } from './converters';

@Injectable({
  providedIn: 'root',
})
export class CheckoutConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService,
    private datePipe: DatePipe
  ) {}

  placeOrder(userId: string, cartId: string, orderToMergeWith?: OrderMergeProposal): Observable<Order> {
    if (orderToMergeWith) {
      let queryParams: any = {
        cartCodeOrType: cartId,
        orderNumber: orderToMergeWith.orderNumber,
        requestedDeliveryDate: this.datePipe.transform(orderToMergeWith.requestedDeliveryDate, 'yyyy-MM-dd'),
        shippingCondition: orderToMergeWith.shippingCondition,
        orderType: orderToMergeWith.orderType,
      };
      if (orderToMergeWith.orderNote) {
        queryParams = {
          ...queryParams,
          orderNote: orderToMergeWith.orderNote,
        };
      }
      return this.httpClient
        .post<Occ.Order>(
          this.occEndpointsService.buildUrl('placeOrderWithMerge', {
            urlParams: {
              userId: userId,
            },
            queryParams,
          }),
          null
        )
        .pipe(this.converterService.pipeable(ORDER_NORMALIZER));
    } else {
      return this.httpClient
        .post<Occ.Order>(
          this.occEndpointsService.buildUrl('placeOrder', {
            urlParams: {
              userId: userId,
            },
            queryParams: {
              cartCodeOrType: cartId,
            },
          }),
          null
        )
        .pipe(this.converterService.pipeable(ORDER_NORMALIZER));
    }
  }

  simulateCart(userId: string, cartId: string): Observable<CartSimulation> {
    return this.httpClient
      .post<Occ.CartSimulation>(
        this.occEndpointsService.buildUrl('simulateCart', {
          urlParams: {
            userId: userId,
            cartCodeOrType: cartId,
          },
        }),
        null
      )
      .pipe(this.converterService.pipeable(CART_SIMULATION_NORMALIZER));
  }

  setResolveOption(
    userId: string,
    cartId: string,
    entry: OrderEntry,
    type: ResolveOptionActionType,
    changeData: AutoResolveChange
  ): Observable<any> {
    return this.httpClient.post(
      this.occEndpointsService.buildUrl('setResolveOption', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
          entryNumber: `${entry.entryNumber}`,
        },
        queryParams: {
          type,
        },
      }),
      changeData
    );
  }

  setOrderNote(userId: string, cartId: string, note: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setOrderNote', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
        queryParams: { note },
      }),
      null,
      { headers }
    );
  }

  setDeliveryNote(userId: string, cartId: string, note: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setDeliveryNote', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
        queryParams: { note },
      }),
      null,
      { headers }
    );
  }

  setMessage(userId: string, cartId: string, message: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setMessage', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
        queryParams: { message },
      }),
      null,
      { headers }
    );
  }

  setGoodsMarking(userId: string, cartId: string, goodsMarking: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setGoodsMarking', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
        queryParams: { goodsMarking },
      }),
      null,
      { headers }
    );
  }

  setCustomerReference(userId: string, cartId: string, customerReference: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setCustomerReference', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
        queryParams: { customerReference },
      }),
      null,
      { headers }
    );
  }

  setPhoneNumber(userId: string, cartId: string, phoneNumber: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setPhoneNumber', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
        queryParams: { phoneNumber },
      }),
      null,
      { headers }
    );
  }

  setInvoiceNote(userId: string, cartId: string, note: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setInvoiceNote', {
        urlParams: {
          userId,
          cartCodeOrType: cartId,
        },
        queryParams: { note },
      }),
      null,
      { headers }
    );
  }

  setCostPlace(userId: string, cartId: string, costPlace: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setCostPlace', {
        urlParams: {
          userId,
          cartCodeOrType: cartId,
        },
        queryParams: { place: costPlace },
      }),
      null,
      { headers }
    );
  }

  setReinvoicing(userId: string, cartId: string, reinvoicing: Reinvoicing): Observable<any> {
    return this.httpClient.put(
      this.occEndpointsService.buildUrl('setReinvoicing', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
      }),
      reinvoicing
    );
  }

  setComment(userId: string, cartId: string, comment: B2BComment): Observable<B2BComment> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.httpClient.patch(
      this.occEndpointsService.buildUrl('setOrUpdateComment', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
      }),
      comment,
      { headers }
    );
  }

  deleteComment(userId: string, cartId: string, comment: B2BComment): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.httpClient.request(
      'DELETE',
      this.occEndpointsService.buildUrl('deleteComment', {
        urlParams: {
          userId: userId,
          cartCodeOrType: cartId,
        },
      }),
      { body: comment, headers: headers }
    );
  }

  loadHolidays(userId: string): Observable<OpeningSchedule> {
    const now = Date.now();
    return this.httpClient.get(
      this.occEndpointsService.buildUrl('holidays', {
        urlParams: { userId },
        queryParams: {
          dateFrom: formatISO(startOfMonth(subMonths(now, 1)), { representation: 'date' }),
          dateTo: formatISO(endOfMonth(addMonths(now, 6)), { representation: 'date' }),
        },
      })
    );
  }

  loadActiveOrderProcess(userId: string, options: { codeOnly?: boolean } = {}): Observable<Order> {
    const { codeOnly } = options;
    return this.httpClient
      .get<Occ.Order>(
        this.occEndpointsService.buildUrl('activeOrderProcess', {
          urlParams: {
            userId,
          },
          queryParams: codeOnly ? { fields: 'code' } : undefined,
        })
      )
      .pipe(this.converterService.pipeable(ORDER_NORMALIZER));
  }

  cancelActiveOrderProcess(userId: string, orderCode: string): Observable<void> {
    return this.httpClient.post<void>(
      this.occEndpointsService.buildUrl('cancelActiveOrderProcess', {
        urlParams: {
          userId,
          orderCode,
        },
      }),
      {}
    );
  }

  updateTaxCode(userId: string, cartId: string, taxCode: string): Observable<void> {
    return this.httpClient.put<void>(
      this.occEndpointsService.buildUrl('taxCode', {
        urlParams: {
          userId,
          cartCodeOrType: cartId,
        },
        queryParams: {
          taxCode,
        },
      }),
      {}
    );
  }
}
